/**
 * Replaces jquery-number (`$('input.number').number(true)`) and the old number.js helpers.
 *
 * The input shows "1,234,567" while typing. Unlike jquery-number there is no `.val()` hook,
 * so read values with getNumber(el) and write them with setNumber(el, v). Submitting the
 * formatted text is fine: the server strips commas via NumberHelper::toValue().
 */

export function number_format(number, decimals, dec_point, thousands_sep) {
    // *     example: number_format(1234.56, 2, ',', ' ');
    // *     return: '1 234,56'
    number = (number + '').replace(',', '').replace(' ', '');
    const n = !isFinite(+number) ? 0 : +number;
    const prec = !isFinite(+decimals) ? 0 : Math.abs(decimals);
    const sep = typeof thousands_sep === 'undefined' ? ',' : thousands_sep;
    const dec = typeof dec_point === 'undefined' ? '.' : dec_point;
    const toFixedFix = (value, p) => {
        const k = Math.pow(10, p);
        return '' + Math.round(value * k) / k;
    };
    const s = (prec ? toFixedFix(n, prec) : '' + Math.round(n)).split('.');
    if (s[0].length > 3) {
        s[0] = s[0].replace(/\B(?=(?:\d{3})+(?!\d))/g, sep);
    }
    if ((s[1] || '').length < prec) {
        s[1] = s[1] || '';
        s[1] += new Array(prec - s[1].length + 1).join('0');
    }
    return s.join(dec);
}

export function number_value(text) {
    text = String(text ?? '');
    return text.indexOf(',') > 0 ? parseFloat(text.replace(/,/g, '')) : parseFloat(text);
}

/** Digits only, optional leading minus: what jquery-number(true) accepts. */
function clean(value) {
    const str = String(value ?? '');
    const negative = /^\s*-/.test(str);
    const digits = str.replace(/[^\d]/g, '').replace(/^0+(?=\d)/, '');
    return (negative ? '-' : '') + digits;
}

function formatClean(raw) {
    if (raw === '' || raw === '-') return raw;
    const negative = raw.startsWith('-');
    const digits = negative ? raw.slice(1) : raw;
    return (negative ? '-' : '') + digits.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
}

/**
 * Format a server-rendered value. Plain decimals such as "1500.00" (DB decimal columns)
 * are rounded like jquery-number(true) does, instead of having the dot stripped.
 */
function formatInitial(value) {
    const str = String(value).trim();
    if (/^-?\d+(\.\d+)?$/.test(str)) return formatClean(String(Math.round(Number(str))));
    return formatClean(clean(str));
}

/** Numeric value of a .number input (0 when empty). */
export function getNumber(target) {
    const el = typeof target === 'string' ? document.querySelector(target) : target;
    if (!el) return 0;
    const n = parseFloat(String(el.value).replace(/,/g, ''));
    return Number.isFinite(n) ? n : 0;
}

/** Set a .number input from a number, keeping the formatted display. */
export function setNumber(target, value) {
    const el = typeof target === 'string' ? document.querySelector(target) : target;
    if (!el) return;
    const n = Math.round(Number(String(value ?? '').replace(/,/g, '')) || 0);
    el.value = el.classList.contains('number') || el.dataset.numberInit ? formatClean(String(n)) : String(n);
}

function onInput(e) {
    const el = e.target;
    const before = el.value;
    const caret = el.selectionStart ?? before.length;
    // Count significant characters left of the caret so it can be restored after reformatting.
    const significantLeft = before.slice(0, caret).replace(/[^\d-]/g, '').length;
    const formatted = formatClean(clean(before));
    if (formatted === before) return;
    el.value = formatted;
    let pos = 0;
    let seen = 0;
    while (pos < formatted.length && seen < significantLeft) {
        if (/[\d-]/.test(formatted[pos])) seen++;
        pos++;
    }
    try {
        el.setSelectionRange(pos, pos);
    } catch (err) {
        // input types without selection support
    }
}

function onClick() {
    this.select();
}

function onBlur() {
    if (this.value === '' || this.value == null) this.value = 0;
}

/** Attach number formatting to one input. Safe to call repeatedly. */
export function attachNumberInput(el) {
    if (el.dataset.numberInit) return;
    el.dataset.numberInit = '1';
    el.setAttribute('autocomplete', 'off');
    if (el.value !== '') el.value = formatInitial(el.value);
    el.addEventListener('input', onInput);
    el.addEventListener('click', onClick);
    el.addEventListener('blur', onBlur);
}

export function initNumberInputs(root = document) {
    root.querySelectorAll('input.number').forEach(attachNumberInput);
}
